import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Conditional from '../../common/conditional';
import CreatableMultiselectDropdown from '../../themed/multiselect/creatable';
import Loader from '../../themed/loader';

import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { getUsers, loadUsers } from '../../../store/users';

import * as models from '../../../utils/models';
import * as service from '../../../services/organizationService';

import { getErrorMessage, getInitials, formatTimeWithRelativeStamp } from '../../../utils/utilities';
import { USER_TYPES } from '../../../utils/constants';

import classNames from './style.module.scss';
import toastService from '../../../services/toastService';

interface NotificationRecipientsProps {
	organizationId: string;
}

const NotificationRecipients: React.FC<NotificationRecipientsProps> = ({ organizationId }) => {
	const [selectedUser, setSelectedUser] = useState<models.CoverDataItem | null>(null);

	const dispatch = useAppDispatch();
	const users = useAppSelector(getUsers);
	const queryClient = useQueryClient();

	const queryKey = useMemo(() => ['notification-recipients', organizationId], [organizationId]);

	const { data: recipients, isFetching } = useQuery<models.NotificationRecipient[]>({
		queryKey,
		queryFn: async () => {
			const result = await service.getNotificationRecipients(organizationId);
			return result.data.recipients;
		},
		enabled: !!organizationId,
		initialData: [],
	});

	const { mutate: addRecipient, isPending: isAdding } = useMutation({
		mutationFn: (user: models.CoverDataItem) => service.addNotificationRecipient(organizationId, user.value),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
			setSelectedUser(null);

			toastService.success('Notification recipient added');
		},
		onError: (error) => toastService.error(getErrorMessage(error)),
	});

	const { mutate: removeRecipient } = useMutation({
		mutationFn: (recipientId: string) => service.removeNotificationRecipient(organizationId, recipientId),
		onMutate: (recipientId: string) => {
			queryClient.setQueryData<models.NotificationRecipient[]>(queryKey, (current) => {
				return (current ?? []).filter((recipient) => recipient._id !== recipientId);
			});
		},
		onSuccess: () => toastService.success('Notification recipient removed'),
		onError: (error) => {
			queryClient.invalidateQueries({ queryKey });
			toastService.error(getErrorMessage(error));
		},
	});

	const eligibleUsersForDropdown = useMemo((): models.CoverDataItem[] => {
		const recipientIds = (recipients ?? []).map((recipient) => recipient._id);

		return users.users
			.filter((user: models.User) => {
				return (
					user.organisationId === organizationId &&
					user.userType === USER_TYPES.EXTERNAL &&
					!recipientIds.includes(user.id as string)
				);
			})
			.map((user: models.User) => ({
				value: user.id,
				label: `${user.firstName} ${user.lastName} (${user.email})`,
			}));
	}, [users.users, organizationId, recipients]);

	useEffect(() => {
		dispatch(loadUsers());

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={classNames.recipients}>
			<h4>Notification Recipients</h4>
			<p>External users who receive report notification emails for this organization</p>

			<div className="d-flex align-items-start mt-3">
				<div style={{ flex: 1 }}>
					<CreatableMultiselectDropdown
						label=""
						placeholder="Select Notification Recipient"
						isMulti={false}
						isLoading={false}
						closeMenuOnSelect
						isSearchable
						options={eligibleUsersForDropdown}
						onSelect={(user) => setSelectedUser(user as models.CoverDataItem)}
						selectedValues={selectedUser as models.CoverDataItem}
						error=""
					/>
				</div>

				<button
					className={classNames.recipientsAddButton + ' ms-3'}
					disabled={!selectedUser || isAdding}
					onClick={() => addRecipient(selectedUser!)}
					style={{ marginTop: '0.5rem' }}
				>
					<i className="bi bi-plus-circle"></i>
					Add Recipient
				</button>
			</div>

			<Loader isVisible={isFetching} />

			<Conditional isVisible={!isFetching}>
				<div className={classNames.recipientsItems}>
					{(recipients ?? []).map((recipient) => (
						<div className={classNames.card} key={recipient._id}>
							<div className={classNames.cardInitials}>
								<span>{getInitials(`${recipient.firstName} ${recipient.lastName}`)}</span>
							</div>

							<div className={classNames.cardDetails}>
								<div className={classNames.cardDetailsName}>
									{recipient.firstName} {recipient.lastName}
								</div>

								<div className={classNames.cardDetailsEmail}>{recipient.email}</div>

								<div className={classNames.cardDetailsMeta}>
									Added by <span>{recipient.addedByName}</span>,{' '}
									{formatTimeWithRelativeStamp(recipient.addedAt)}
								</div>
							</div>

							<button
								className={classNames.cardRemove}
								onClick={() => removeRecipient(recipient._id)}
							>
								<i className="bi bi-trash"></i>
							</button>
						</div>
					))}
				</div>

				<Conditional isVisible={(recipients ?? []).length === 0}>
					<div className={classNames.recipientsEmpty}>
						No notification recipients added yet
					</div>
				</Conditional>
			</Conditional>
		</div>
	);
};

export default NotificationRecipients;
